import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { ResultGrid } from '../../../../../grid/ResultGrid';
import { Column } from '../../../../../grid/Column';
import { DeleteIcon } from '../../../../../icon/DeleteIcon';
import { EditIcon } from '../../../../../icon/EditIcon';
import { ExternalLinkIcon } from '../../../../../table/ExternalLinkIcon';
import { InfoIcon } from '../../../../../icon/InfoIcon';
import {
  parseAndShortenFloat,
} from '../../../../../../helperFunctions/helperFunctions';
import {
  bulkEditVolcanoEvents as bulkEditVolcanoEventsAc,
  cancelEdit as cancelEditAc,
  cancelEditConfirmation as cancelEditConfirmationAc,
  openEditConfirmation as openEditConfirmationAc,
  startEdit as startEditAc,
} from '../../../../../../redux/volcanoes/event/data/volcanoEventDataActions';
import { createInitialForm, formToJson } from '../edit/volcanoEventEditModel';
import { checkboxCell, textCell } from '../../../../../grid/resultGridCells';
import {
  minColumns,
  sort as defaultSort,
  visibleColumns as defaultColumns,
} from '../../../../../../redux/volcanoes/event/data/volcanoEventReduxConstants';

const renderEditLink = id => (
  <Link aria-label={`Edit Volcano Event ${id}`} to={`/view/hazards/volcano/event-update/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

const renderEarthquakeLink = id => (
  <Link aria-label={`Earthquake Event Id ${id}`} to={`/view/hazards/earthquake/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <ExternalLinkIcon ariaLabel="Link To Earthquake Event" tooltip="View Related Earthquake Event" />
  </Link>
);

const renderTsunamiLink = id => (
  <Link aria-label={`Tsunami Id ${id}`} to={`/view/hazards/tsunami/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <ExternalLinkIcon ariaLabel="Link To Tsunami Event" tooltip="View Related Tsunami Event" />
  </Link>
);

const renderInfoLink = id => (
  <Link aria-label={`Volcano Event Id ${id}`} to={`/view/hazards/volcano/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <InfoIcon />
  </Link>
);

const handleBeforeUnload = (event) => {
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = 'leaving earthquake';
};

class VolcanoEventDataGridUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.focus = this.focus.bind(this);
    this.handleYesClick = this.handleYesClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEditToggle = this.handleEditToggle.bind(this);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
    this.deleteCancel = null;
    this.state = {
      modal: false,
      selectedId: null,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      deleting,
      onDeleteCloseModal,
      saving,
      cancelEditConfirmation,
      errors,
      cancelEdit,
    } = this.props;
    if (!deleting && prevProps.deleting) {
      this.closeModal(() => {
        onDeleteCloseModal();
      });
    }
    if (!saving && prevProps.saving) {
      cancelEditConfirmation();
      window.removeEventListener('beforeunload', handleBeforeUnload, true);
      if (!errors.size) {
        cancelEdit();
        onDeleteCloseModal(); // reload
      }
    }
  }

  openModal(selectedId) {
    this.setState({
      modal: true,
      selectedId,
    });
  }

  closeModal(callback) {
    this.setState({
      modal: false,
      selectedId: null,
    }, callback);
  }

  handleDeleteClick(id) {
    this.openModal(id);
  }

  focus() {
    this.deleteCancel.focus();
  }

  handleYesClick() {
    const { deleteVolcanoEvent, history } = this.props;
    const { selectedId } = this.state;
    deleteVolcanoEvent({ id: selectedId, history });
  }

  handleSubmit(data) {
    const { bulkEditVolcanoEvents } = this.props;
    bulkEditVolcanoEvents({ data });
  }

  handleEditToggle(edit) {
    const { startEdit, cancelEdit, volcanoEvents } = this.props;
    if (edit) {
      const items = {};
      const initialValues = { items };
      volcanoEvents.forEach((item) => {
        items[`${item.get('id')}`] = createInitialForm(item).toJS();
      });
      startEdit({ initialValues });
      window.addEventListener('beforeunload', handleBeforeUnload, true);
    } else {
      cancelEdit();
      window.removeEventListener('beforeunload', handleBeforeUnload, true);
    }
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortData, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortData(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      volcanoEvents,
      dataManager,
      sort,
      visibleColumns,
      searchParams,
      deleting,
      editConfirmationOpen,
      saving,
      formData,
      cancelEditConfirmation,
      openEditConfirmation,
      initialFormData,
      initialValues,
      editing,
      errors,
    } = this.props;

    const { modal } = this.state;

    return (
      <div className="haz-data-grid-container">

        <Modal isOpen={modal || deleting} onOpened={this.focus} backdrop="static">
          <ModalHeader>Delete Volcano Event</ModalHeader>
          <ModalBody>
            {deleting ? 'Deleting...' : 'Are you sure you want to delete the volcano event?'}
          </ModalBody>
          <ModalFooter>
            <Button disabled={deleting} color="danger" onClick={this.handleYesClick}>Delete</Button>
            {' '}
            <Button disabled={deleting} color="secondary" innerRef={(ref) => { this.deleteCancel = ref; }} onClick={() => this.closeModal()}>Cancel</Button>
          </ModalFooter>
        </Modal>

        <ResultGrid
          data={volcanoEvents}
          sort={sort}
          columnChooser
          visibleColumns={visibleColumns}
          handleColumnsToggle={this.handleToggleColumns}
          download
          downloadPrefix="volcano-events"
          downloadSearchParams={searchParams}
          edit={dataManager}
          editing={editing}
          handleEditToggle={this.handleEditToggle}
          initialValues={initialValues}
          handleSubmit={this.handleSubmit}
          editConfirmationOpen={editConfirmationOpen}
          hazardName="Volcano Events"
          saving={saving}
          handleCancel={cancelEditConfirmation}
          formData={formData}
          initialFormData={initialFormData}
          openEditConfirmation={openEditConfirmation}
          formToJson={formToJson}
          defaultColumns={defaultColumns}
          defaultSort={defaultSort}
          minColumns={minColumns}
        >
          {dataManager && (
          <Column alwaysVisible downloadable={false} id="del" cell={({ row }) => (<DeleteIcon handleClick={() => this.handleDeleteClick(row.get('id'))} />)} width={30}>
            <DeleteIcon />
          </Column>
          )}
          {dataManager && (
          <Column alwaysVisible downloadable={false} id="edit" cell={({ row }) => renderEditLink(row.get('id'))} width={30}>
            <EditIcon />
          </Column>
          )}
          <Column alwaysVisible={dataManager} id="id" width={50} sortable>Id</Column>
          {dataManager && (
          <Column alwaysVisible id="significant" cell={({ value, row }) => checkboxCell(row, 'significant', value, errors, editing)} width={60} sortable>Significant</Column>
          )}
          <Column id="year" width={45} sortable helpModalId="volEventYear" cell={({ value, row }) => textCell(row, 'year', value, errors, editing)}>Year</Column>
          <Column id="month" width={35} sortable helpModalId="month" cell={({ value, row }) => textCell(row, 'month', value, errors, editing)}>Mo</Column>
          <Column id="day" width={35} sortable helpModalId="day" cell={({ value, row }) => textCell(row, 'day', value, errors, editing)}>Dy</Column>
          <Column id="tsunamiEventId" width={35} helpModalId="volEventTsunami" cell={({ value }) => (value && renderTsunamiLink(value))}>Tsu</Column>
          <Column id="earthquakeEventId" width={35} helpModalId="volEventEarthquake" cell={({ value }) => (value && renderEarthquakeLink(value))}>Eq</Column>
          <Column id="moreInfo" downloadable={false} helpModalId="volEventMoreInfo" width={45} cell={({ row }) => renderInfoLink(row.get('id'))}>More Info</Column>
          <Column id="name" width={200} sortable helpModalId="volLocName">Name</Column>
          <Column id="location" width={200} sortable helpModalId="volLocRegion">Location</Column>
          {dataManager && (
          <Column id="volcanoLocationId" width={100} sortable>NCEI ID</Column>
          )}
          {dataManager && (
          <Column id="volcanoLocationNewNum" width={100} sortable helpModalId="volLocNewNum">Volcano Number</Column>
          )}
          {dataManager && (
          <Column id="volcanoLocationNum" width={100} sortable helpModalId="volLocNum">Previous Volcano Number</Column>
          )}
          <Column id="country" width={200} sortable helpModalId="volEventCountry">Country</Column>
          <Column id="latitude" width={100} sortable helpModalId="latitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Latitude</Column>
          <Column id="longitude" width={100} sortable helpModalId="longitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Longitude</Column>
          <Column id="elevation" width={56} sortable helpModalId="volLocElevation">Elevation (m)</Column>
          <Column id="morphology" width={150} sortable helpModalId="volLocType">Type</Column>
          {dataManager && (
          <Column id="eruption" width={80} sortable helpModalId="volEventEruption" cell={({ value, row }) => checkboxCell(row, 'eruption', value, errors, editing)}>Eruption</Column>
          )}
          <Column id="status" width={120} sortable helpModalId="volLocStatus">Status</Column>
          <Column id="timeErupt" width={45} sortable helpModalId="volLocTimeErupt">Time Erupt</Column>
          <Column id="vei" width={45} sortable helpModalId="volEventVei" cell={({ value, row }) => textCell(row, 'vei', value, errors, editing)}>VEI</Column>
          <Column id="agent" width={50} sortable helpModalId="volEventAgent" cell={({ value, row }) => textCell(row, 'agent', value, errors, editing)}>Agent</Column>
          <Column id="deaths" width={50} sortable helpModalId="volEventDeaths" cell={({ value, row }) => textCell(row, 'deaths', value, errors, editing)}>Deaths</Column>
          <Column id="deathsAmountOrder" width={80} sortable helpModalId="volEventDeathsDescription" cell={({ value, row }) => textCell(row, 'deathsAmountOrder', value, errors, editing)}>Death Description</Column>
          <Column id="missing" width={50} sortable helpModalId="volEventMissing" cell={({ value, row }) => textCell(row, 'missing', value, errors, editing)}>Missing</Column>
          <Column id="missingAmountOrder" width={80} sortable helpModalId="volEventMissingDescription" cell={({ value, row }) => textCell(row, 'missingAmountOrder', value, errors, editing)}>Missing Description</Column>
          <Column id="injuries" width={50} sortable helpModalId="volEventInjuries" cell={({ value, row }) => textCell(row, 'injuries', value, errors, editing)}>Injuries</Column>
          <Column id="injuriesAmountOrder" width={80} sortable helpModalId="volEventInjuriesDescription" cell={({ value, row }) => textCell(row, 'injuriesAmountOrder', value, errors, editing)}>Injuries Description</Column>
          <Column id="damageMillionsDollars" width={60} sortable helpModalId="volEventDamage" cell={({ value, row }) => textCell(row, 'damageMillionsDollars', value, errors, editing)}>Damage ($Mil)</Column>
          <Column id="damageAmountOrder" width={80} sortable helpModalId="volEventDamageDescription" cell={({ value, row }) => textCell(row, 'damageAmountOrder', value, errors, editing)}>Damage Description</Column>
          <Column id="housesDestroyed" width={80} sortable helpModalId="volEventHousesDestroyed" cell={({ value, row }) => textCell(row, 'housesDestroyed', value, errors, editing)}>Houses Destroyed</Column>
          <Column id="housesDestroyedAmountOrder" width={110} sortable helpModalId="volEventHousesDestroyedDescription" cell={({ value, row }) => textCell(row, 'housesDestroyedAmountOrder', value, errors, editing)}>Houses Destroyed Description</Column>
          <Column id="deathsTotal" width={50} sortable helpModalId="volEventTotalDeaths" cell={({ value, row }) => textCell(row, 'deathsTotal', value, errors, editing)}>Total Deaths</Column>
          <Column id="deathsAmountOrderTotal" width={80} sortable helpModalId="volEventTotalDeathsDescription" cell={({ value, row }) => textCell(row, 'deathsAmountOrderTotal', value, errors, editing)}>Total Death Description</Column>
          <Column id="missingTotal" width={50} sortable helpModalId="volEventTotalMissing" cell={({ value, row }) => textCell(row, 'missingTotal', value, errors, editing)}>Total Missing</Column>
          <Column id="missingAmountOrderTotal" width={80} sortable helpModalId="volEventTotalMissingDescription" cell={({ value, row }) => textCell(row, 'missingAmountOrderTotal', value, errors, editing)}>Total Missing Description</Column>
          <Column id="injuriesTotal" width={50} sortable helpModalId="volEventTotalInjuries" cell={({ value, row }) => textCell(row, 'injuriesTotal', value, errors, editing)}>Total Injuries</Column>
          <Column id="injuriesAmountOrderTotal" width={80} sortable helpModalId="volEventTotalInjuriesDescription" cell={({ value, row }) => textCell(row, 'injuriesAmountOrderTotal', value, errors, editing)}>Total Injuries Description</Column>
          <Column id="damageMillionsDollarsTotal" width={90} sortable helpModalId="volEventTotalDamage" cell={({ value, row }) => textCell(row, 'damageMillionsDollarsTotal', value, errors, editing)}>Total Damage ($Mil)</Column>
          <Column id="damageAmountOrderTotal" width={90} sortable helpModalId="volEventTotalDamageDescription" cell={({ value, row }) => textCell(row, 'damageAmountOrderTotal', value, errors, editing)}>Total Damage Description</Column>
          <Column id="housesDestroyedTotal" width={90} sortable helpModalId="volEventTotalHousesDestroyed" cell={({ value, row }) => textCell(row, 'housesDestroyedTotal', value, errors, editing)}>Total Houses Destroyed</Column>
          <Column id="housesDestroyedAmountOrderTotal" width={130} sortable helpModalId="volEventTotalHousesDestroyedDescription" cell={({ value, row }) => textCell(row, 'housesDestroyedAmountOrderTotal', value, errors, editing)}>Total Houses Destroyed Description</Column>
        </ResultGrid>
      </div>
    );
  }
}

VolcanoEventDataGridUnwrapped.propTypes = {
  volcanoEvents: ImmutablePropTypes.list.isRequired,
  dataManager: PropTypes.bool.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  sortData: PropTypes.func.isRequired,
  searchParams: PropTypes.string.isRequired,
  deleting: PropTypes.bool.isRequired,
  deleteVolcanoEvent: PropTypes.func.isRequired,
  history: PropTypes.shape({}).isRequired,
  onDeleteCloseModal: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  bulkEditVolcanoEvents: PropTypes.func.isRequired,
  editConfirmationOpen: PropTypes.bool.isRequired,
  formData: ImmutablePropTypes.map.isRequired,
  initialFormData: ImmutablePropTypes.map.isRequired,
  openEditConfirmation: PropTypes.func.isRequired,
  cancelEditConfirmation: PropTypes.func.isRequired,
  errors: ImmutablePropTypes.map.isRequired,
  startEdit: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  editing: PropTypes.bool.isRequired,
  initialValues: ImmutablePropTypes.map.isRequired,
};

const mapStateToProps = state => ({
  saving: state.getIn(['volcanoEventDataPage', 'saving']),
  editConfirmationOpen: state.getIn(['volcanoEventDataPage', 'editConfirmationOpen']),
  formData: state.getIn(['volcanoEventDataPage', 'formData']),
  initialFormData: state.getIn(['volcanoEventDataPage', 'initialFormData']),
  errors: state.getIn(['volcanoEventDataPage', 'errors']),
  editing: state.getIn(['volcanoEventDataPage', 'editing']),
  initialValues: state.getIn(['volcanoEventDataPage', 'initialValues']),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  bulkEditVolcanoEvents: bulkEditVolcanoEventsAc,
  openEditConfirmation: openEditConfirmationAc,
  cancelEditConfirmation: cancelEditConfirmationAc,
  startEdit: startEditAc,
  cancelEdit: cancelEditAc,
}, dispatch);

export const VolcanoEventDataGrid = connect(mapStateToProps, mapDispatchToProps)(VolcanoEventDataGridUnwrapped);
